import React, { useEffect, useRef, useState } from "react";
import { gsap } from "../../lib/gsap";
import profile from "../../data/profile";

export default function VideoIntro() {
  const videoRef = useRef(null);
  const nameRef = useRef(null);
  const roleRef = useRef(null);
  const eyebrowRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => {});
    const tl = gsap.timeline({ delay: 0.35 });
    tl.fromTo(eyebrowRef.current, { opacity: 0, y: -12 }, { opacity: 1, y: 0, duration: 0.6 })
      .fromTo(nameRef.current, { opacity: 0, x: -50 }, { opacity: 1, x: 0, duration: 0.9, ease: "power3.out" }, "-=0.2")
      .fromTo(roleRef.current, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.55 }, "-=0.4");
    return () => tl.kill();
  }, []);

  // Listen for loader-dismissed event to unmute + restart from beginning
  useEffect(() => {
    function onDismiss() {
      const v = videoRef.current;
      if (!v) return;
      try {
        v.currentTime = 0;
        v.muted = false;
        v.play().catch(() => {});
        setMuted(false);
      } catch (e) {}
    }
    window.addEventListener("loader-dismissed", onDismiss);
    return () => window.removeEventListener("loader-dismissed", onDismiss);
  }, []);

  function toggleMute() {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }
  function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  }
  function scrollNext() {
    const main = document.querySelector(".main-scroller");
    if (main) main.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  }

  return (
    <section className="snap-section grain" data-testid="video-intro">
      {/* Ambient blurred backdrop */}
      <video
        src="/assets/intro.mp4"
        autoPlay muted loop playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "blur(38px) saturate(1.05) brightness(0.55)", transform: "scale(1.2)", zIndex: 0 }}
      />
      {/* Main video */}
      <video
        ref={videoRef}
        data-testid="intro-video"
        src="/assets/intro.mp4"
        autoPlay muted loop playsInline
        className="absolute inset-0 w-full h-full object-cover"
        style={{ zIndex: 1 }}
      >
        <track
          kind="captions"
          src="/assets/intro-captions.vtt"
          srcLang="en"
          label="English"
          default
        />
      </video>
      {/* Cinematic overlays */}
      <div className="absolute inset-0" style={{
        zIndex: 2,
        background: "radial-gradient(120% 90% at 50% 40%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.5) 55%, rgba(0,0,0,0.85) 100%)",
      }} />
      <div className="absolute inset-0" style={{
        zIndex: 2,
        background: "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 70%, rgba(10,10,11,0.98) 100%)",
      }} />

      {/* Content */}
      <div className="relative h-full w-full flex flex-col justify-end px-6 md:px-16 pb-24" style={{ zIndex: 5 }}>
        <p ref={eyebrowRef} className="font-mono text-xs md:text-sm tracking-[0.32em] text-[var(--accent)] uppercase mb-4">
          &nbsp;
        </p>
        <h1 ref={nameRef} className="font-serif leading-[0.85] tracking-tight" style={{ fontSize: "var(--hero-name-size)", fontWeight: 400 }}>
          <span className="block">{profile.name.first}</span>
          <span className="block italic">{profile.name.last}.</span>
        </h1>
        <p ref={roleRef} className="mt-5 max-w-xl text-sm md:text-base text-[color:rgba(245,242,236,0.75)]">
          {profile.roles.detailed} · Based in {profile.location.based}
        </p>
      </div>

      {/* Bottom-left controls */}
      <div className="absolute bottom-6 left-6 flex items-center gap-2" style={{ zIndex: 6 }}>
        <button onClick={togglePlay} data-testid="intro-play-toggle"
          className="w-9 h-9 rounded-full border border-[rgba(245,242,236,0.35)] flex items-center justify-center hover:bg-white/10 transition">
          {playing ? (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><rect x="2" y="1" width="3" height="10" rx="1"/><rect x="7" y="1" width="3" height="10" rx="1"/></svg>
          ) : (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor"><polygon points="2,1 11,6 2,11"/></svg>
          )}
        </button>
        <button onClick={toggleMute} data-testid="intro-mute-toggle"
          className="w-9 h-9 rounded-full border border-[rgba(245,242,236,0.35)] flex items-center justify-center hover:bg-white/10 transition">
          {muted ? (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M2 5.5h2.5L8 3v10l-3.5-2.5H2V5.5z" fill="currentColor" stroke="none"/>
              <line x1="10" y1="5" x2="14" y2="11"/><line x1="14" y1="5" x2="10" y2="11"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
              <path d="M2 5.5h2.5L8 3v10l-3.5-2.5H2V5.5z" fill="currentColor" stroke="none"/>
              <path d="M10.5 5.5C11.8 6.5 12.5 7.2 12.5 8s-.7 1.5-2 2.5"/>
              <path d="M12 3.5C14 5 15 6.4 15 8s-1 3-3 4.5"/>
            </svg>
          )}
        </button>
        <span className="font-mono text-[10px] tracking-[0.25em] text-[color:rgba(245,242,236,0.55)] ml-2">TAP FOR SOUND</span>
      </div>

      {/* Scroll cue */}
      <button data-testid="intro-scroll-cue" onClick={scrollNext}
        className="absolute bottom-6 right-6 flex flex-col items-center gap-2 group" style={{ zIndex: 6 }}>
        <span className="font-mono text-[10px] tracking-[0.35em] text-[color:rgba(245,242,236,0.65)] uppercase">Scroll</span>
        <span className="block w-px h-10 bg-[rgba(245,242,236,0.55)] group-hover:h-14 transition-all" />
      </button>
    </section>
  );
}
